/**
 * SVG Sanitizer Utility
 *
 * Client-side defense-in-depth SVG sanitization using DOMPurify.
 * Removes malicious scripts and event handlers from SVG code.
 *
 * @module svg-sanitizer
 */

import DOMPurify from 'dompurify';

/**
 * Sanitize SVG string to remove malicious content
 *
 * Whitelist approach:
 * - Allows standard SVG elements (svg, path, circle, rect, etc.)
 * - Removes all script tags and event handlers
 * - Removes potentially dangerous attributes (onerror, onload, onclick, etc.)
 *
 * @param svgString - Raw SVG code (potentially unsafe)
 * @returns Sanitized SVG string (safe for rendering)
 *
 * @example
 * ```typescript
 * const unsafeSvg = '<svg><script>alert("XSS")</script><path d="..."/></svg>';
 * const safeSvg = sanitizeSvg(unsafeSvg);
 * // Result: '<svg><path d="..."></path></svg>'
 * ```
 */
export function sanitizeSvg(svgString: string | null): string | null {
  if (!svgString) {
    return null;
  }

  try {
    // Configure DOMPurify for SVG sanitization
    const config = {
      // Use SVG namespace
      NAMESPACE: 'http://www.w3.org/2000/svg',

      // Allow only SVG elements
      ALLOWED_TAGS: [
        'svg',
        'path',
        'circle',
        'rect',
        'polygon',
        'polyline',
        'line',
        'ellipse',
        'g',
        'defs',
        'clipPath',
        'mask',
        'linearGradient',
        'radialGradient',
        'stop',
        'text',
        'tspan',
        'use',
        'symbol',
        'style', // Allow embedded CSS (DOMPurify sanitizes dangerous CSS)
        'title', // Allow accessibility title
        'desc', // Allow accessibility description
      ],

      // Allow safe SVG attributes
      ALLOWED_ATTR: [
        'xmlns',
        'viewBox',
        'width',
        'height',
        'x',
        'y',
        'x1',
        'y1',
        'x2',
        'y2',
        'cx',
        'cy',
        'r',
        'rx',
        'ry',
        'd',
        'fill',
        'fill-rule',
        'clip-rule',
        'stroke',
        'stroke-width',
        'stroke-linecap',
        'stroke-linejoin',
        'stroke-dasharray',
        'stroke-dashoffset',
        'opacity',
        'fill-opacity',
        'stroke-opacity',
        'transform',
        'id',
        'class',
        'points',
        'offset',
        'stop-color',
        'stop-opacity',
        'clip-path',
        'mask',
        'gradientUnits',
        'gradientTransform',
        'xlink:href',
        'href',
        'font-size',
        'font-family',
        'font-weight',
        'text-anchor',
        'dominant-baseline',
      ],

      // Forbid event handlers and dangerous attributes
      FORBID_ATTR: [
        'onclick',
        'onload',
        'onerror',
        'onmouseover',
        'onmouseout',
        'onmouseenter',
        'onmouseleave',
        'onfocus',
        'onblur',
        'onchange',
        'oninput',
        'onsubmit',
        'onkeydown',
        'onkeyup',
        'onkeypress',
        'ontouchstart',
        'ontouchend',
        'ontouchmove',
        'onpointerdown',
        'onpointerup',
        'onpointermove',
      ],

      // Forbid tags that can execute code
      FORBID_TAGS: [
        'script',
        'iframe',
        'embed',
        'object',
        'foreignObject',
        'animate',
        'animateMotion',
        'animateTransform',
        'set',
      ],

      // Keep SVG structure
      KEEP_CONTENT: true,

      // Return DOM instead of string for better control
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
    };

    // Sanitize SVG
    const sanitized = DOMPurify.sanitize(svgString, config);

    // Return sanitized SVG
    return sanitized;
  } catch (error) {
    console.error('[SVG Sanitizer] Failed to sanitize SVG:', error);
    return null;
  }
}

/**
 * Validate SVG size (should be <50KB)
 *
 * @param svgString - SVG string to validate
 * @returns True if size is acceptable
 */
export function validateSvgSize(svgString: string | null): boolean {
  if (!svgString) {
    return true; // null is valid
  }

  const sizeInBytes = new Blob([svgString]).size;
  const maxSizeInBytes = 50 * 1024; // 50KB

  return sizeInBytes <= maxSizeInBytes;
}

/**
 * Validate and sanitize SVG string
 *
 * Combines size validation and sanitization.
 *
 * @param svgString - Raw SVG code
 * @returns Sanitized SVG or null if invalid
 *
 * @example
 * ```typescript
 * const svg = validateAndSanitizeSvg(userInput);
 * if (svg) {
 *   // Safe to render
 *   element.innerHTML = svg;
 * } else {
 *   // Invalid or too large
 *   console.error('Invalid SVG');
 * }
 * ```
 */
export function validateAndSanitizeSvg(svgString: string | null): string | null {
  if (!svgString) {
    return null;
  }

  // Check size
  if (!validateSvgSize(svgString)) {
    console.warn('[SVG Sanitizer] SVG exceeds 50KB size limit');
    return null;
  }

  // Sanitize
  return sanitizeSvg(svgString);
}
